import {
  AspectRatio,
  Box,
  Button,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Spinner,
  Text,
  useColorMode,
} from '@chakra-ui/react'
import { debounce } from 'lodash'
import { useWallet } from '@solana/wallet-adapter-react'
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { WalletMultiButton } from '../wallet-ui'
import { defaultButtonStyle } from '../wallet-ui/common'
import { NftMetadata } from '../../utils/nftmetaData.type'
import {
  useNftFromMetadata,
  useWalletNftMetadatas,
  WalletNftMetadataV2,
  WalletNftV2,
  useWalletUnifiedNftMetadatas,
  WalletUnifiedNftMetadataV2,
  useNftFromUnifiedMetadata,
  getNFTDisplayData,
  convertToSelectedNFTType,
} from '../../utils/useWalletNFTs'
import { SearchIcon } from '@chakra-ui/icons'
import { SelectedNFTType } from '../../utils/utils'
import { PublicKey } from '@solana/web3.js'

const DisplayNFTToSelect: FC<{
  metadata: WalletNftMetadataV2
  checked: NonNullable<SelectedNFTType>[]
  stopLoading?: boolean
  onAddInput: (value: SelectedNFTType) => void
  onDeleteInput: (value: SelectedNFTType) => void
}> = ({ metadata, checked, stopLoading, onDeleteInput, onAddInput }) => {
  const { colorMode } = useColorMode()
  const isDarkMode = colorMode === 'dark'
  const nftRes = useNftFromMetadata(metadata, stopLoading)

  const nftMetadata = nftRes.nft
  return !nftMetadata ? (
    <Box
      padding='14px'
      cursor='pointer'
      borderRadius='5px'
      w={['7rem', '9rem']}
      h={'5rem'}
      onClick={async () => {
        console.log(metadata, metadata.data.data, metadata.data.mint);
        await nftRes.refetch()
        checked.some((c) => c.pubkey.equals(metadata.pubkey))
        ? onDeleteInput({pubkey: new PublicKey(metadata.data.mint), name: metadata.data.data.name, isCoreNFT: false})
        : onAddInput({pubkey: new PublicKey(metadata.data.mint), name: metadata.data.data.name, isCoreNFT: false})
      }
        
      }
      bg={isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}
      boxShadow={
        checked.some((c) => c.pubkey.equals(metadata.pubkey))
          ? 'rgba(0, 0, 0, 0.4) 0px 2px 4px'
          : ''
      }
    >
      <Text fontSize='0.7rem'>{metadata.data.data.name}</Text>
    </Box>
  ) : (
    <Box
      paddingY='4px'
      cursor='pointer'
      borderRadius='5px'
      w={['7rem', '9rem']}
      bg={'transparent'}
      key={metadata.pubkey.toBase58()}
      onClick={() =>
        checked.some((c) => c.pubkey.equals(metadata.pubkey))
          ? onDeleteInput(nftMetadata)
          : onAddInput(nftMetadata)
      }
      border={
        checked.includes(nftMetadata)
          ? isDarkMode
            ? '3px solid #ccc'
            : '3px solid #fff'
          : 'none'
      }
      boxShadow={
        checked.some((c) => c.pubkey.equals(metadata.pubkey))
          ? 'rgba(0, 0, 0, 0.4) 0px 2px 4px'
          : ''
      }
    >
      <Text fontSize='0.7rem'>
        {nftMetadata.name ?? metadata.data.data.name}
      </Text>

      <AspectRatio maxWidth={'144px'} ratio={1}>
        <Image
          src={nftMetadata.image}
          alt={(nftMetadata.name ?? metadata.data.data.name) + ' NFT'}
          style={{
            maxHeight: '144px',
            borderRadius: '6px',
            boxShadow: 'sm',
            filter: 'brightness(90%)',
            objectFit: 'cover',
          }}
        ></Image>
      </AspectRatio>
    </Box>
  )
}

// New unified component that can handle both legacy and core NFTs
const DisplayUnifiedNFTToSelect: FC<{
  unifiedMetadata: WalletUnifiedNftMetadataV2
  checked: NonNullable<SelectedNFTType>[]
  stopLoading?: boolean
  onAddInput: (value: SelectedNFTType) => void
  onDeleteInput: (value: SelectedNFTType) => void
}> = ({ unifiedMetadata, checked, stopLoading, onDeleteInput, onAddInput }) => {
  const { colorMode } = useColorMode()
  const isDarkMode = colorMode === 'dark'
  const nftRes = useNftFromUnifiedMetadata(unifiedMetadata, stopLoading)
  const displayData = getNFTDisplayData(unifiedMetadata)

  const nftMetadata = nftRes.nft
  const isChecked = checked.some((c) => c.pubkey.equals(displayData.pubkey))

  return !nftMetadata ? (
    <Box
      padding='14px'
      cursor='pointer'
      borderRadius='5px'
      w={['7rem', '9rem']}
      h={'5rem'}
      onClick={async () => {
        console.log('Unified metadata:', unifiedMetadata, 'Display data:', displayData)
        await nftRes.refetch()
        isChecked
          ? onDeleteInput(convertToSelectedNFTType(unifiedMetadata))
          : onAddInput(convertToSelectedNFTType(unifiedMetadata))
      }}
      bg={isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}
      boxShadow={isChecked ? 'rgba(0, 0, 0, 0.4) 0px 2px 4px' : ''}
    >
      <Text fontSize='0.7rem'>{displayData.name}</Text>
      <Text fontSize='0.6rem' color={isDarkMode ? '#ccc' : '#666'}>
        {unifiedMetadata.standard === 'core' ? 'Core NFT' : 'Legacy NFT'}
      </Text>
    </Box>
  ) : (
    <Box
      paddingY='4px'
      cursor='pointer'
      borderRadius='5px'
      w={['7rem', '9rem']}
      bg={'transparent'}
      key={displayData.pubkey.toBase58()}
      onClick={() =>
        isChecked ? onDeleteInput(nftMetadata) : onAddInput(nftMetadata)
      }
      border={
        isChecked
          ? isDarkMode
            ? '3px solid #ccc'
            : '3px solid #fff'
          : 'none'
      }
      boxShadow={isChecked ? 'rgba(0, 0, 0, 0.4) 0px 2px 4px' : ''}
    >
      <Text fontSize='0.7rem'>{nftMetadata.name ?? displayData.name}</Text>
      <Text fontSize='0.6rem' color={isDarkMode ? '#ccc' : '#666'}>
        {unifiedMetadata.standard === 'core' ? 'Core NFT' : 'Legacy NFT'}
      </Text>

      <AspectRatio maxWidth={'144px'} ratio={1}>
        <Image
          src={nftMetadata.image}
          alt={(nftMetadata.name ?? displayData.name) + ' NFT'}
          style={{
            maxHeight: '144px',
            borderRadius: '6px',
            boxShadow: 'sm',
            filter: 'brightness(90%)',
            objectFit: 'cover',
          }}
        />
      </AspectRatio>
    </Box>
  )
}

interface NFTSelectorProps {
  isLoading?: boolean
  nftVerificationLoading?: boolean
  values?: WalletNftMetadataV2[]
  maxNfts?: number
  selectedNFT?: SelectedNFTType
  onChange: (values: SelectedNFTType[]) => void
  useUnified?: boolean // New prop to enable unified NFT fetching
}

const NFTSelector: FC<NFTSelectorProps> = ({
  maxNfts,
  onChange,
  selectedNFT,
  nftVerificationLoading,
  useUnified = true, // Default to false for backward compatibility
}) => {
  const [searchKey, setSearchKey] = useState<string | undefined>()
  const { metadatas, loading: metadatasLoading } = useWalletNftMetadatas(!useUnified)
  const { unifiedMetadatas, loading: unifiedMetadatasLoading } = useWalletUnifiedNftMetadatas(useUnified,)

  // Choose which data to use based on useUnified prop
  const dataToUse = useUnified ? unifiedMetadatas : metadatas
  const loadingState = useUnified ? unifiedMetadatasLoading : metadatasLoading

  useEffect(() => {
    if (useUnified) {
      console.log('found unified metadatas', unifiedMetadatas.length, unifiedMetadatas)
    } else {
      console.log('found metadatas', metadatas.length, metadatas, metadatas[0] && metadatas[0].data)
    }
  }, [useUnified, metadatas, unifiedMetadatas])

  console.log({ selectedNFT })

  const collections = useMemo(() => {
    return []
  }, [dataToUse])

  const metadatasForSelection = useMemo(() => {
    if (useUnified) {
      return unifiedMetadatas.filter((m) => {
        const displayData = getNFTDisplayData(m)
        return !searchKey
          ? true
          : displayData.name.toLowerCase().includes(searchKey.toLowerCase()) ||
              displayData.symbol.toLowerCase().includes(searchKey.toLowerCase())
      })
    } else {
      return metadatas.filter((m) => {
        return !searchKey
          ? true
          : (m.data.data.name ?? '').toLowerCase().includes(searchKey.toLowerCase()) ||
              (m.data.data.symbol ?? '').toLowerCase().includes(searchKey.toLowerCase())
      })
    }
  }, [useUnified, metadatas, unifiedMetadatas, searchKey])

  // const [checked, setChecked] = useState<NftMetadata[]>([])
  const { colorMode } = useColorMode()
  const isDarkMode = colorMode === 'dark'
  const { connected } = useWallet()

  const onAddInput = useCallback(
    (value: SelectedNFTType) => {
      onChange([value])
    },
    [onChange]
  )

  const onDeleteInput = useCallback(
    (value: SelectedNFTType) => {
      onChange([])
    },
    [onChange]
  )

  /*
  
  old one with multiple inputs
  
  const onAddInput = useCallback(
    (value: WalletNftV2) => {
      if (maxNfts && maxNfts != 1 && checked.length >= maxNfts) return

      if (maxNfts === 1) {
        checked.splice(0, checked.length)
      }

      checked.push(value)
      setChecked([...checked])
      onChange(checked)
    },
    [checked]
  )

  const onDeleteInput = useCallback(
    (value: NftMetadata) => {
      for (let i = 0; i < checked.length; i++) {
        if (checked[i].pubkey.toBase58() === value.pubkey.toBase58()) {
          checked.splice(i, 1)
          setChecked([...checked])
          onChange(checked)
          break
        }
      }
    },
    [checked, onChange]
  )*/

  const debouncedChangeHandler = useCallback(
    debounce((event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchKey(event.target.value)
    }, 300),
    []
  )

  if (!connected) {
    return <WalletMultiButton></WalletMultiButton>
  }

  return (
    <Box
      maxH={['30rem', '34rem']}
      bg='rgba(238, 238, 238, 0.1)'
      border={isDarkMode ? '1px solid #5a5a5a' : '1px solid #CBCBCB'}
      borderRadius='5px'
      overflowY='scroll'
      padding='20px'
      paddingBottom='50px'
      maxW='100%'
    >
      <Box>
        <Text fontWeight={600}>
          Search to see to the image (type in 2 letters)
          {useUnified && (
            <Text fontSize='sm' color={isDarkMode ? '#ccc' : '#666'}>
              Showing both Legacy and Core NFTs
            </Text>
          )}
        </Text>
        <InputGroup py='0.7rem'>
          <Input
            onChange={debouncedChangeHandler}
            border='1px solid #5a5a5a'
            placeholder='Search NFTs by name or symbol and see the image...'
            _placeholder={{
              color: isDarkMode
                ? 'rgba(255, 255, 255, 0.8)'
                : 'rgba(0, 0, 0, 0.4)',
            }}
            isInvalid={false}
            maxWidth='42rem'
            bg={'transparent'}
            color={isDarkMode ? '#fff' : '#232323'}
          />
          <InputLeftElement
            top='inherit'
            pointerEvents='none'
            children={<SearchIcon color='gray.300' />}
          />
        </InputGroup>
      </Box>
      {nftVerificationLoading && (
        <Box>
          <Text paddingY='1rem'>
            Verifying selected NFT ... <Spinner></Spinner>
          </Text>
        </Box>
      )}
      <SimpleGrid
        gap={['1rem', '1.2rem']}
        columns={{ base: 2, sm: 2, md: 2, lg: 3, xl: 4 }}
      >
        {loadingState && (
          <Box>
            <Text>Loading NFTs from wallet...</Text>
            <Spinner
              color={isDarkMode ? 'white' : 'black'}
              mt={3}
              size={'md'}
            ></Spinner>{' '}
          </Box>
        )}
        {!!selectedNFT && (
          <Box
            cursor='pointer'
            borderRadius='5px'
            w={'9rem'}
            bg={isDarkMode ? '#5a5a5a' : '#fff'}
            key={selectedNFT.pubkey.toBase58()}
            onClick={() => onDeleteInput(selectedNFT)}
            border={isDarkMode ? '3px solid #5a5a5a' : '3px solid #fff'}
            boxShadow={'rgba(0, 0, 0, 0.4) 0px 2px 4px'}
            overflow='hidden'
          >
            <Text fontSize={'0.7rem'}>{selectedNFT.name}</Text>
            <AspectRatio maxWidth={'150px'} ratio={1} overflow='hidden'>
              {!selectedNFT.image ? (
                <Box
                  width='100%'
                  h='100%'
                  bg={isDarkMode ? '#222222' : '#d1d1d1'}
                ></Box>
              ) : (
                <Image
                  alt={selectedNFT.name + ' NFT'}
                  src={selectedNFT.image}
                  style={{
                    maxHeight: '144px',
                    borderRadius: '6px',
                    boxShadow: 'sm',
                    filter: 'brightness(90%)',
                    objectFit: 'cover',
                  }}
                ></Image>
              )}
            </AspectRatio>
          </Box>
        )}
        {!selectedNFT &&
          metadatasForSelection.length < 500 &&
          metadatasForSelection.map((input) => {
            if (useUnified) {
              return (
                <DisplayUnifiedNFTToSelect
                  key={getNFTDisplayData(input as WalletUnifiedNftMetadataV2).mint}
                  unifiedMetadata={input as WalletUnifiedNftMetadataV2}
                  checked={[]}
                  onAddInput={onAddInput}
                  onDeleteInput={onDeleteInput}
                />
              )
            } else {
              return (
                <DisplayNFTToSelect
                  key={(input as WalletNftMetadataV2).data.mint}
                  metadata={input as WalletNftMetadataV2}
                  checked={[]}
                  onAddInput={onAddInput}
                  onDeleteInput={onDeleteInput}
                />
              )
            }
          })}

        {!selectedNFT &&
          metadatasForSelection.length > 500 &&
          metadatasForSelection.map((input) => {
            if (useUnified) {
              return (
                <DisplayUnifiedNFTToSelect
                  key={getNFTDisplayData(input as WalletUnifiedNftMetadataV2).mint}
                  unifiedMetadata={input as WalletUnifiedNftMetadataV2}
                  checked={[]}
                  stopLoading={(searchKey ?? '').length < 2}
                  onAddInput={onAddInput}
                  onDeleteInput={onDeleteInput}
                />
              )
            } else {
              return (
                <DisplayNFTToSelect
                  key={(input as WalletNftMetadataV2).data.mint}
                  metadata={input as WalletNftMetadataV2}
                  checked={[]}
                  stopLoading={(searchKey ?? '').length < 2}
                  onAddInput={onAddInput}
                  onDeleteInput={onDeleteInput}
                />
              )
            }
          })}
      </SimpleGrid>
      
    </Box>
  )
}

export default NFTSelector
